// src/pages/CompanyPages/ReschedulePage.js

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  Button,
  MenuItem,
  Grid,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";

import { useThemeMode } from "../../components/ThemeModeContext";
import axiosInstance from "../../Services/axiosConfig";
import Sidebar from "../../components/CompanySidebar";
import Footer from "../../components/Footer";
import FeedbackButton from "../../components/FeedbackButton";
import FeedbackSnackbar from "../../components/FeedbackSnackbar";

export default function ReschedulePage() {
  const { companyId } = useParams();
  const token = localStorage.getItem("authToken");

  // Estado de carregamento e agendamentos
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);

  // Estado para a seleção do agendamento "No-Show" e data para reagendar
  const [selectedAppointment, setSelectedAppointment] = useState("");
  const [newDate, setNewDate] = useState("");
  const [quantity, setQuantity] = useState(1); // Adicionado para nova regra

  // Estados para Snackbar (caso queira usar)
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Modo Dark/Light
  const { mode } = useThemeMode();
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          background: {
            default: mode === "light" ? "#FAF5E4" : "#121212",
            paper: mode === "light" ? "#FFFFFF" : "#1E1E1E",
          },
          text: {
            primary: mode === "light" ? "#1A202C" : "#FFFFFF",
          },
        },
      }),
    [mode]
  );

  // Fecha Snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Busca appointments marcados como "No-Show"
  const fetchNoShowAppointments = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `company-appointments-no-show/${token}/${companyId}/`
      );
      if (response.data.status === "success") {
        const appointmentList = Object.entries(response.data.data).map(([id, appointment]) => ({
          id,
          ...appointment,
        }));
        setAppointments(appointmentList);
      } else {
        setAppointments([]);
        Swal.fire("Error", response.data.message, "error");
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
      Swal.fire("Error", "Failed to fetch appointments. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Reagendar
  const handleReschedule = async () => {
    if (!selectedAppointment || !newDate) {
      Swal.fire("Warning", "Please select an appointment and a new date.", "warning");
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post(
        `company-reschedule/${token}/${selectedAppointment}/${companyId}/`,
        {
          new_date: newDate,
          quantity: quantity, // Enviar quantidade
          timezone: "America/Sao_Paulo", // Adicionando timezone como padrão
        }
      );
      if (response.data.status === "success") {
        Swal.fire("Success", "Appointment rescheduled successfully!", "success");
        setSelectedAppointment("");
        setNewDate("");
        setQuantity(1); // Resetar quantidade após reagendamento
        fetchNoShowAppointments(); // atualiza lista após reagendamento
      } else {
        Swal.fire("Error", response.data.message, "error");
      }
    } catch (error) {
      console.error("Error rescheduling appointment:", error);
      Swal.fire("Error", "Failed to reschedule appointment. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Busca inicial (componentDidMount)
  useEffect(() => {
    fetchNoShowAppointments();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Box display="flex" flexDirection="column" minHeight="100vh" bgcolor="background.default">
        
        {/* Layout horizontal: Sidebar + conteúdo */}
        <Box display="flex" flexGrow={1}>
          <Sidebar companyId={companyId} />

          {/* Conteúdo Principal */}
          <Box flex={1} p={3} color="text.primary">
            <Typography variant="h4" gutterBottom>
              Reschedule Appointments
            </Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
                <CircularProgress />
              </Box>
            ) : (
              <Box mb={3}>
                {/* Select "No-Show" Appointment */}
                <TextField
                  select
                  label="Select No-Show Appointment"
                  value={selectedAppointment}
                  onChange={(e) => setSelectedAppointment(e.target.value)}
                  fullWidth
                  required
                  margin="normal"
                >
                  {appointments.map((appointment) => (
                    <MenuItem key={appointment.id} value={appointment.id}>
                      {`${appointment.service} - ${appointment.client} - ${new Date(
                        appointment.date
                      ).toLocaleString()}`}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Data para reagendar */}
                <TextField
                  label="New Date"
                  type="datetime-local"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  margin="normal"
                />

                {/* Quantidade para reagendamento */}
                <TextField
                  label="Quantity"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  fullWidth
                  required
                  margin="normal"
                />

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleReschedule}
                  disabled={loading}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : "Reschedule"}
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {/* Botão de Feedback (Darkmode + feedback) */}
        <FeedbackButton />

        {/* Footer */}
        <Footer />

        {/* Snackbar para feedback */}
        <FeedbackSnackbar
          open={openSnackbar}
          handleClose={handleCloseSnackbar}
          message={snackbarMsg}
          severity={snackbarSeverity}
        />
      </Box>
    </ThemeProvider>
  );
}
